import { FeatureCollection, Point } from 'geojson'
import palette from 'core-system/Themes/palette'
import { Locations } from 'shared/Router/Locations'
import {
  DashboardWorksiteDistribution,
  DashboardWorksiteProgram,
} from '../dashboardTypes'

export type GeographicView = 'offices' | 'compliance'

export const OFFICE_DOT_COLOR = palette.secondary.purple700
export const COMPLIANT_DOT_COLOR = palette.success.green500
export const NONCOMPLIANT_DOT_COLOR = palette.warning.yellow500

const MIN_RADIUS = 7
const MAX_RADIUS = 26

export const worksiteProgramSummary = (
  programs: DashboardWorksiteProgram[]
): string => {
  if (!programs.length) {
    return 'No programs at site'
  }
  return [...programs]
    .sort(
      (a, b) => b.enrolled - a.enrolled || a.name.localeCompare(b.name)
    )
    .map((program) => program.name)
    .join(' · ')
}

export const formatWorksiteEnrollment = (enrolled: number): string =>
  `${enrolled.toLocaleString()} enrolled`

export const formatWorksitePeople = (people: number): string =>
  `${people.toLocaleString()} people`

export const locationDetailPath = (locationKey: string): string =>
  Locations.DashboardLocation.replace(
    ':locationKey',
    encodeURIComponent(locationKey)
  )

export const geographicSitePath = (
  view: GeographicView,
  locationKey: string
): string | null => {
  if (view === 'compliance') {
    return Locations.Compliance
  }
  if (!locationKey) {
    return null
  }
  return locationDetailPath(locationKey)
}

export interface WorksiteHoverDetails {
  name: string
  people: string
  enrolled: string
  programs: string
}

export const hoverTooltipPosition = ({
  x,
  y,
  width,
  height,
  containerWidth,
  containerHeight,
  padding = 8,
  gap = 10,
}: {
  x: number
  y: number
  width: number
  height: number
  containerWidth: number
  containerHeight: number
  padding?: number
  gap?: number
}): { left: number; top: number } => {
  const maxLeft = Math.max(padding, containerWidth - width - padding)
  const left = Math.min(Math.max(x - width / 2, padding), maxLeft)

  const below = y + gap
  const above = y - height - gap
  let top = above >= padding ? above : below
  const maxTop = Math.max(padding, containerHeight - height - padding)
  top = Math.min(Math.max(top, padding), maxTop)
  return { left, top }
}

export const complianceStatusLabel = (compliant: boolean): string =>
  compliant ? 'Compliant' : 'Non-compliant'

const isUnitedStatesWorksite = (locationKey: string | null): boolean => {
  const country = locationKey?.split('|')[2]?.trim().toUpperCase()
  return country === 'US'
}

export const worksitesForGeographicView = (
  sites: DashboardWorksiteDistribution[],
  view: GeographicView
): DashboardWorksiteDistribution[] => {
  if (view !== 'compliance') {
    return sites
  }
  return sites.filter((site) => isUnitedStatesWorksite(site.locationKey))
}

export const worksiteDotColor = (
  site: Pick<DashboardWorksiteDistribution, 'compliant'>,
  view: GeographicView
): string => {
  if (view === 'offices') {
    return OFFICE_DOT_COLOR
  }
  return site.compliant ? COMPLIANT_DOT_COLOR : NONCOMPLIANT_DOT_COLOR
}

export const worksiteHoverDetails = (properties: {
  name?: string
  employeeCount?: number | string
  enrolled?: number | string
  programSummary?: string
}): WorksiteHoverDetails => ({
  name: properties.name || 'Worksite',
  people: formatWorksitePeople(Number(properties.employeeCount) || 0),
  enrolled: formatWorksiteEnrollment(Number(properties.enrolled) || 0),
  programs: properties.programSummary || 'No programs at site',
})

export const worksiteFeatureCollection = (
  sites: DashboardWorksiteDistribution[],
  view: GeographicView = 'offices'
): FeatureCollection<Point> => {
  const plotted = worksitesForGeographicView(sites, view).filter(
    (site) => site.lat != null && site.lng != null && site.employeeCount > 0
  )
  const maxCount = Math.max(1, ...plotted.map((site) => site.employeeCount))
  const features = plotted.map((site) => ({
    type: 'Feature' as const,
    geometry: {
      type: 'Point' as const,
      coordinates: [site.lng as number, site.lat as number] as [number, number],
    },
      properties: {
        id: site.id,
        name: site.label,
        employeeCount: site.employeeCount,
        enrolled: site.enrolled,
        locationKey: site.locationKey,
        color: worksiteDotColor(site, view),
        programSummary: worksiteProgramSummary(site.programs),
        radius:
          MIN_RADIUS + (site.employeeCount / maxCount) * (MAX_RADIUS - MIN_RADIUS),
      },
  }))
  // Larger dots first so smaller worksites paint on top where they overlap.
  features.sort(
    (a, b) =>
      (b.properties?.employeeCount ?? 0) - (a.properties?.employeeCount ?? 0)
  )
  return { type: 'FeatureCollection', features }
}
